import { GateOverrideHost } from './lib/gateOverride';
import { EntryAskHost } from './lib/entryAsk';
import { useEffect, useState } from 'react';
import { BoardPage } from './components/board/BoardPage';
import { BoardsPage } from './components/BoardsPage';
import { LoginPage, ResetPasswordPage } from './components/LoginPage';
import { AdminPage } from './components/AdminPage';
import { InvitePage } from './components/InvitePage';
import { AppLinkPage } from './components/AppLinkPage';
import { GuestPage } from './components/GuestPage';
import { ProfilePage } from './components/ProfilePage';
import { TeamSettingsPage, TeamsPage } from './components/TeamsPage';
import { Toaster } from './components/ui/ui';
import { PersonDialogHost } from './components/PersonDialog';
import { TimerBar } from './components/TimerBar';
import { FormFillPage } from './components/forms/FormFillPage';
import { FormEditorPage } from './components/forms/FormEditorPage';
import { FormConfirmedPage, PublicFormPage } from './components/forms/PublicFormPage';
import { useMe, usePublicConfig } from './lib/auth';
import { setInstanceAccent, setThemeChoice } from './lib/theme';
import { useLocation } from './lib/router';
import { useRealtimeConnection } from './lib/realtime';
import { hasAnyDraft } from './lib/drafts';
import { OfflineBar } from './components/OfflineBar';

export function App() {
  const { data: me, isLoading, error, refetch } = useMe();
  const { pathname } = useLocation();
  const update = useSwUpdate();
  const [updateLater, setUpdateLater] = useState(false);
  useRealtimeConnection(!!me);
  const { data: config } = usePublicConfig();
  // Installationsfarbe gilt überall, wo kein Board- oder Teamkontext aktiv ist (B03)
  useEffect(() => setInstanceAccent(config?.branding?.accent ?? null), [config?.branding?.accent]);
  // Persönliche Darstellung aus dem Konto (lokal vorgemerkt für den nächsten Start)
  useEffect(() => {
    if (me?.appearance) setThemeChoice(me.appearance.theme);
  }, [me?.appearance]);

  let page: React.ReactNode;
  // Öffentliche Formulare: ohne Konto, unabhängig von der Anmeldung
  if (/^\/formular\/[A-Za-z0-9_-]{20,64}$/.test(pathname)) page = <PublicFormPage key={pathname} token={pathname.slice(10)} />;
  else if (pathname === '/formular-bestaetigt') page = <FormConfirmedPage />;
  else if (isLoading) page = <div className="p-8 text-sm text-[var(--ink-soft)]">Lade …</div>;
  else if (error && !me)
    page = (
      <div className="flex min-h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-lg font-semibold">Keine Verbindung</p>
        <p className="text-sm text-[var(--ink-soft)]">KaBa ist gerade nicht erreichbar. Prüfe deine Internetverbindung.</p>
        <button className="min-h-11 rounded-lg bg-accent px-4 text-sm font-medium text-on-accent" onClick={() => void refetch()}>
          Erneut versuchen
        </button>
      </div>
    );
  else if (pathname === '/reset-password') page = <ResetPasswordPage />;
  else if (pathname === '/einladung') page = <InvitePage />;
  else if (pathname === '/team-einladung') page = <InvitePage kind="team" />;
  else if (pathname.startsWith('/g/')) page = <GuestPage />;
  else if (!me) page = <LoginPage />;
  else if (pathname === '/app-verbinden') page = <AppLinkPage />;
  else if (pathname === '/admin') page = <AdminPage />;
  else if (pathname === '/profil') page = <ProfilePage />;
  else if (pathname === '/teams') page = <TeamsPage />;
  else if (/^\/teams\/[0-9a-f-]{36}$/.test(pathname)) page = <TeamSettingsPage key={pathname} teamId={pathname.slice(7)} />;
  else if (/^\/f\/[0-9a-f-]{36}$/.test(pathname)) page = <FormFillPage key={pathname} formId={pathname.slice(3)} />;
  else if (/^\/b\/[0-9a-f-]{36}\/formulare\/[0-9a-f-]{36}$/.test(pathname)) page = <FormEditorPage key={pathname} boardId={pathname.slice(3, 39)} formId={pathname.slice(-36)} />;
  else {
    const m = pathname.match(/^\/b\/([0-9a-f-]{36})/);
    page = m ? <BoardPage key={m[1]} boardId={m[1]!} /> : <BoardsPage />;
  }

  return (
    <>
      {page}
      {me && <TimerBar />}
      {me && <PersonDialogHost />}
      {me && <OfflineBar />}
      <Toaster />
      {me && <GateOverrideHost />}
      {me && <EntryAskHost />}
      {update && !updateLater && (
        // Unten statt oben: verdeckt weder Titel noch Menü oder Schließen; lässt sich wegschieben
        <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] z-[70] flex justify-center px-4 sm:bottom-6">
          <div role="status" className="pointer-events-auto flex items-center gap-3 rounded-xl bg-slate-900 py-1.5 pr-1.5 pl-4 text-sm text-white shadow-lg dark:bg-slate-100 dark:text-slate-900">
            Neue Version verfügbar.
            <button className="min-h-10 rounded-lg px-2 font-semibold underline" onClick={update}>
              Jetzt laden
            </button>
            <button className="min-h-10 rounded-lg px-2 opacity-80" onClick={() => setUpdateLater(true)}>
              Später
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Service Worker: neue Version wird angeboten, nicht ungefragt neu geladen */
function useSwUpdate(): (() => void) | null {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  useEffect(() => {
    if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
    let reloading = false;
    // Nur neu laden, wenn die Person der neuen Version zugestimmt hat
    // (beim allerersten Installieren übernimmt der SW ohne Neuladen)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading || !acceptedUpdate) return;
      reloading = true;
      location.reload();
    });
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      if (reg.waiting && navigator.serviceWorker.controller) {
        // Beim Laden der Seite wartet schon eine neue Version (z. B. nach Neuladen ohne „Jetzt laden“):
        // ohne offene Entwürfe sofort übernehmen – sonst bliebe der Hinweis nach jedem Neuladen stehen.
        if (performance.now() < 15_000 && !hasAnyDraft()) {
          acceptedUpdate = true;
          reg.waiting.postMessage('skipWaiting');
          return;
        }
        setWaiting(reg.waiting);
      }
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        sw?.addEventListener('statechange', () => {
          if (sw.state === 'installed' && navigator.serviceWorker.controller) setWaiting(sw);
        });
      });
      setInterval(() => void reg.update(), 60 * 60 * 1000);
    });
  }, []);
  return waiting
    ? () => {
        acceptedUpdate = true;
        waiting.postMessage('skipWaiting');
      }
    : null;
}

let acceptedUpdate = false;
